import {forwardRef, ReactNode, TextareaHTMLAttributes} from 'react'
import {Label} from '@components/ui/label'
import {InputHelpText} from '@components/ui/input-help-text'
import {cn} from '@utilities/helpers'

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    className?: string
    label?: ReactNode
    name?: string
    typeIcon?: ReactNode
    errorMessage?: ReactNode
    helpText?: ReactNode
    placeholder?: string
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({label, name, typeIcon, errorMessage, helpText, className, disabled, placeholder, ...rest}, ref) => {
        return (
            <div className={cn('flex min-h-23 w-full flex-col gap-1.5', className)}>
                {label && <Label htmlFor={name}>{label}</Label>}
                <div className="flex flex-row items-center">
                    <div
                        className={cn(
                            'relative flex w-full cursor-pointer items-center justify-center gap-1 rounded-lg border border-neutral-300 bg-white transition',
                            !!errorMessage && 'border-danger-300',
                            disabled
                                ? 'cursor-not-allowed bg-neutral-100 text-neutral-500'
                                : cn(
                                      'hover:shadow-xs hover:ring-4',
                                      errorMessage
                                          ? 'hover:border-danger-300 hover:ring-danger-100'
                                          : 'hover:border-primary-300 hover:ring-primary-100'
                                  )
                        )}
                    >
                        {typeIcon}
                        <textarea
                            className="h-full w-full flex-1 resize-none border-none px-3.5 py-3 text-neutral-900 text-md outline-none placeholder:text-neutral-400 focus:outline-none disabled:cursor-not-allowed scrollbar"
                            ref={ref}
                            name={name}
                            placeholder={placeholder}
                            disabled={disabled}
                            {...rest}
                        />
                    </div>
                </div>
                <InputHelpText error={errorMessage} helpText={helpText} />
            </div>
        )
    }
)

TextArea.displayName = 'TextArea'
